import type { Candidate, Job } from '@core/types';
import { computeSkillOverlapFitment } from '@core/utils/skillFitment';

export interface SkillOverlapResult {
  score: number;
  matched: string[];
  missing: string[];
  bonus: string[];
}

function normStr(s: string): string {
  return s.trim().toLowerCase();
}

/**
 * Extends computeSkillOverlapFitment with synonym expansion via the taxonomy alias map.
 * synonymMap: alias (lowercase) -> canonical_name (lowercase)
 */
export function computeSkillOverlap(
  candidate: Candidate,
  job: Job,
  synonymMap: Map<string, string>
): SkillOverlapResult {
  function expandSkills(skills: string[]): Set<string> {
    const expanded = new Set<string>();
    for (const skill of skills) {
      const norm = normStr(skill);
      expanded.add(norm);
      const canonical = synonymMap.get(norm);
      if (canonical) expanded.add(normStr(canonical));
    }
    return expanded;
  }

  const required = job.skills_required ?? [];
  const candidateSkills = candidate.parsed_skills ?? [];

  if (required.length === 0) {
    // Fall back to legacy scorer
    const legacy = computeSkillOverlapFitment(candidate, job);
    return {
      score: legacy?.fitment_score ?? 0,
      matched: (legacy?.fitment_json?.matched as string[]) ?? [],
      missing: (legacy?.fitment_json?.missing as string[]) ?? [],
      bonus: [],
    };
  }

  const expandedRequired = expandSkills(required);
  const expandedCandidate = expandSkills(candidateSkills);
  const expandedNiceToHave = expandSkills((job as { skills_nice_to_have?: string[] }).skills_nice_to_have ?? []);

  const matched: string[] = [];
  const missing: string[] = [];

  for (const skill of required) {
    const norm = normStr(skill);
    const canonical = synonymMap.get(norm) ?? norm;
    if (expandedCandidate.has(norm) || expandedCandidate.has(canonical)) {
      matched.push(skill);
    } else {
      missing.push(skill);
    }
  }

  const bonus: string[] = [];
  for (const skill of candidateSkills) {
    const norm = normStr(skill);
    if (!expandedRequired.has(norm) && expandedNiceToHave.has(norm)) {
      bonus.push(skill);
    }
  }

  const score = required.length > 0 ? Math.round((matched.length / required.length) * 100) : 0;

  return { score, matched, missing, bonus };
}
